import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle2, Loader2, Landmark, FileText, ExternalLink, ClipboardCheck, Building2, MessageSquare } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { BankAccountDetailsDialog, BankAccountDetails } from "./BankAccountDetailsDialog";
import { VideoPlayer } from "./VideoPlayer";

interface JourneyOntraportStepProps {
  stepId: string;
  userId: string;
  ontraportTag?: string;
  tagName?: string;
  guidanceContent: string;
  isCompleted: boolean;
  onComplete: () => void;
  videoUrl?: string;
  videoRequiredWatchPercent?: number;
  initialVideoProgress?: number;
}

export function JourneyOntraportStep({
  stepId,
  userId,
  guidanceContent,
  isCompleted,
  onComplete,
  videoUrl,
  videoRequiredWatchPercent = 95,
  initialVideoProgress = 0,
}: JourneyOntraportStepProps) {
  const [isConfirming, setIsConfirming] = useState(false);
  const [showBankDetailsDialog, setShowBankDetailsDialog] = useState(false);
  const { toast } = useToast();

  const handleVideoProgress = async (percent: number) => {
    try {
      const { data: existingProgress } = await supabase
        .from('user_journey_step_progress')
        .select('action_data, status')
        .eq('user_id', userId)
        .eq('step_id', stepId)
        .maybeSingle();

      const statusToKeep =
        (existingProgress?.status as 'locked' | 'active' | 'completed' | null) ||
        (isCompleted ? 'completed' : 'active');

      await supabase
        .from('user_journey_step_progress')
        .upsert(
          {
            user_id: userId,
            step_id: stepId,
            status: statusToKeep,
            action_data: {
              ...((existingProgress?.action_data as Record<string, unknown> | null) || {}),
              video_watch_percent: percent,
            },
          },
          { onConflict: 'user_id,step_id' }
        );
    } catch (error) {
      console.error('Error saving video progress:', error);
    }
  };

  const handleBankDetailsSubmit = async (details: BankAccountDetails) => {
    setIsConfirming(true);
    try {
      // Get user's UNA
      const { data: unaData } = await supabase
        .from('unas')
        .select('id, name, treasury_secretary_id')
        .eq('treasury_secretary_id', userId)
        .single();

      if (!unaData) {
        throw new Error('No UNA found for user');
      }

      const unaName = unaData.name;

      // Get treasury secretary profile
      const { data: profileData } = await supabase
        .from('profiles')
        .select('full_name')
        .eq('id', userId)
        .single();

      const treasurySecretaryName = profileData?.full_name || 'Secretary Treasurer';

      // Save bank details to user_journey_state
      const { data: existingState } = await supabase
        .from('user_journey_state')
        .select('journey_form_data')
        .eq('user_id', userId)
        .single();

      const updatedFormData = {
        ...(existingState?.journey_form_data as Record<string, string | number | boolean | null> || {}),
        bank_account: JSON.stringify({
          bank_name: details.bank_name,
          bank_address: details.bank_address,
          account_number: details.account_number,
          opened_at: details.opened_at,
        }),
      };

      // Update journey state with bank details
      await supabase
        .from('user_journey_state')
        .update({
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          journey_form_data: updatedFormData as any,
          updated_at: new Date().toISOString(),
        })
        .eq('user_id', userId);

      // Generate Bank Account Opening Minutes
      const openedDate = new Date(details.opened_at).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });

      const minutesContent = `
# Minutes of the Secretary Treasurer
## ${unaName}

**Date:** ${openedDate}

**Action Taken:** Establishment of Bank Account

---

### Resolution

The Secretary Treasurer of ${unaName} hereby records that a bank account has been successfully established as follows:

**Financial Institution:** ${details.bank_name}

**Bank Address:** ${details.bank_address}

**Account Number:** ${details.account_number}

**Date Opened:** ${openedDate}

This account shall serve as the primary operating account for ${unaName} for receiving contributions and making authorized disbursements in accordance with the organization's constitution.

---

**Recorded By:** ${treasurySecretaryName}

**Date:** ${openedDate}
      `.trim();

      // Store the generated document
      await supabase
        .from('generated_documents')
        .insert({
          una_id: unaData.id,
          document_name: 'Bank Account Opening Minutes',
          document_type: 'minutes',
          document_content: minutesContent,
          merge_data: {
            bank_name: details.bank_name,
            bank_address: details.bank_address,
            account_number: details.account_number,
            opened_date: openedDate,
            treasury_secretary_name: treasurySecretaryName,
            una_name: unaName,
          },
          status: 'generated',
          created_by: userId,
        });

      // Mark step as completed
      const { error } = await supabase
        .from('user_journey_step_progress')
        .upsert({
          user_id: userId,
          step_id: stepId,
          status: 'completed',
          completed_at: new Date().toISOString(),
          verified_completion: true,
          action_data: { 
            self_reported: true, 
            confirmed_at: new Date().toISOString(),
            bank_details: {
              bank_name: details.bank_name,
              bank_address: details.bank_address,
            }
          },
        }, { onConflict: 'user_id,step_id' });

      if (error) throw error;

      // Unlock next step
      const { data: currentStep } = await supabase
        .from('journey_steps')
        .select('step_number')
        .eq('id', stepId)
        .single();

      if (currentStep) {
        const { data: nextStep } = await supabase
          .from('journey_steps')
          .select('id')
          .eq('step_number', currentStep.step_number + 1)
          .eq('is_active', true)
          .single();

        if (nextStep) {
          await supabase
            .from('user_journey_step_progress')
            .upsert({
              user_id: userId,
              step_id: nextStep.id,
              status: 'active',
              started_at: new Date().toISOString(),
            }, { onConflict: 'user_id,step_id' });
        }
      }

      setShowBankDetailsDialog(false);

      toast({
        title: "Banking Setup Confirmed!",
        description: "Your Bank Account Opening Minutes are ready and saved with your documents.",
      });

      onComplete();
    } catch (error) {
      console.error('Error completing step:', error);
      toast({
        title: "Error",
        description: "Failed to confirm banking setup. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Video Section */}
      {videoUrl && (
        <div className="space-y-4">
          <VideoPlayer
            videoUrl={videoUrl}
            onProgress={handleVideoProgress}
            onComplete={() => handleVideoProgress(videoRequiredWatchPercent)}
            requiredWatchPercent={videoRequiredWatchPercent}
            initialProgress={initialVideoProgress}
          />
        </div>
      )}

      {isCompleted && (
        <div className="flex items-center justify-center py-2 text-green-600">
          <CheckCircle2 className="w-5 h-5 mr-2" />
          <span className="font-medium">Banking setup confirmed</span>
        </div>
      )}

      {/* Header Section */}
      <div className="text-center space-y-2">
        <h2 className="text-xl font-bold">Now it's time to Open your Bank Account</h2>
        <p className="text-muted-foreground">
          Opening a business bank account for your UNA is the most hands-on part - but essential.
          You're not officially onboarded until it's done. Many have done it successfully - just follow the steps and you'll be ready.
        </p>
      </div>


      {/* Mindset & Approach */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Landmark className="h-5 w-5 text-primary" />
            Mindset & Approach
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <ul className="space-y-2 text-sm">
            <li className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
              <span>Relax, be normal, and follow the steps</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
              <span>You're not doing anything shady - just something uncommon</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
              <span>Banking is about relationships. Be polite, curious, and personable</span>
            </li>
          </ul>
          <Alert className="border-primary/20 bg-primary/5">
            <AlertDescription className="text-sm">
              <strong>Say:</strong> "I was just made treasurer of an unincorporated association and need to open a business account."
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      {/* Bank Selection */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Building2 className="h-5 w-5 text-primary" />
            Bank Selection
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm font-medium text-green-700">✓ Chase has shown to be the smoothest</p>
          <p className="text-sm text-muted-foreground">Big banks like Wells Fargo, BofA, US Bank, PNC also work.</p>
          <p className="text-sm text-destructive">✗ Avoid online-only, local, or regional banks (Ally, Capital One, AMEX) - they don't support this model.</p>
          <p className="text-sm font-medium text-primary">The bank must have a California presence. So long as they have California presence, you should be fine.</p>
        </CardContent>
      </Card>

      {/* Banker Communication */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <MessageSquare className="h-5 w-5 text-primary" />
            Banker Communication
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <p>• Book an appointment if possible</p>
          <p>• Avoid the word "nonprofit" at first—say "unincorporated association"</p>
          <p>• Later, clarify it is nonprofit and faith-based</p>
          <Alert className="border-primary/20 bg-primary/5 mt-3">
            <AlertDescription className="text-sm">
              <strong>Business type:</strong> "All other miscellaneous schools and instruction" (NAICS Code 611699)
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      {/* What to Bring */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <ClipboardCheck className="h-5 w-5 text-primary" />
            What to Bring
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ol className="space-y-2">
            {[
              'EIN Letter from the IRS',
              'California UNA Registration',
              'Banking Minutes (both versions)',
              '2 forms of ID (primary & secondary)',
              'Banking instructions page (for you, not the banker)'
            ].map((item, i) => (
              <li key={i} className="flex items-center gap-3 text-sm">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 text-primary text-xs font-medium flex items-center justify-center">{i + 1}</span>
                {item}
              </li>
            ))}
          </ol>
          <p className="text-xs text-muted-foreground mt-3">
            Leave out: Constitution, Survivorship, and Duties/Resolutions minutes — these confuse bankers
          </p>
        </CardContent>
      </Card>

      {/* Process Tips */}
      <Card className="border-amber-200 bg-amber-50/50">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <FileText className="h-5 w-5 text-amber-600" />
            Process Tips
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <p>• Most accounts take <strong>60–90 minutes</strong> to open</p>
          <p>• Bring smiles, stay flexible, and treat the banker like a partner</p>
          <p>• If one branch is difficult, just try another</p>
          <p className="text-amber-800 font-medium">• Make sure you are NOT listed as the beneficial owner — you are the Secretary/Treasurer, not the owner</p>
          <p className="text-amber-800 font-medium">• Request an "unincorporated association" or "organization" account — NOT a nonprofit account</p>
          <p>• These are nonprofits, but not typical nonprofits — a standard nonprofit account doesn't fit this model</p>
        </CardContent>
      </Card>

      {/* After Setup */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            After Setup
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2 text-sm">
            <li>• Fund the account (usually $50–$100 minimum; Chase gives 30 days)</li>
            <li>• Order debit cards and checks</li>
            <li>• Ask if the banker gets a bonus for opening accounts — help them win too</li>
            <li>• Send a thank you card. Build goodwill.</li>
          </ul>
        </CardContent>
      </Card>

      {/* Full Banking Guide - At Bottom */}
      <Card className="border-primary bg-primary/5">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <FileText className="h-6 w-6 text-primary flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-base">Full Banking Instructions</h3>
                <p className="text-sm text-muted-foreground">
                  Detailed step-by-step guide with everything you need to open your bank account successfully.
                </p>
              </div>
            </div>
            <Button asChild variant="default" className="gap-2 whitespace-nowrap">
              <a href="https://wealthysteward.com/banking" target="_blank" rel="noopener noreferrer">
                <ExternalLink className="w-4 h-4" />
                View Full Guide
              </a>
            </Button>
          </div>
        </CardContent>
      </Card>


      {!isCompleted && (
        <>
          <Alert className="border-green-200 bg-green-50 dark:bg-green-950/20">
            <Landmark className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-800 dark:text-green-200">
              Once your bank account is open, click the button below to confirm completion.
              This will unlock your <strong>strategy session</strong> where we'll discuss next steps for your UNA.
            </AlertDescription>
          </Alert>

          <div className="flex justify-center">
            <Button onClick={() => setShowBankDetailsDialog(true)} className="gap-2" size="lg">
              <CheckCircle2 className="w-4 h-4" />
              I've Set Up My Bank Account
            </Button>
          </div>

          <BankAccountDetailsDialog
            open={showBankDetailsDialog}
            onClose={() => setShowBankDetailsDialog(false)}
            onSubmit={handleBankDetailsSubmit}
            isSubmitting={isConfirming}
          />
        </>
      )}
    </div>
  );
}
